import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  FileCheck, 
  AlertTriangle, 
  RefreshCw,
  Upload,
  School,
  CheckCircle2,
  XCircle,
  Clock,
  BarChart3,
  Activity,
  Target,
  Users,
  Repeat,
  ShieldCheck,
  AlertCircle,
  Gauge,
  Brain
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import DataEntryExecutiveTab from "@/components/DataEntryExecutiveTab";
import DataEntryInsightsTab from "@/components/DataEntryInsightsTab";
import DataEntryBlockPerformanceTab from "@/components/DataEntryBlockPerformanceTab";
import DataEntrySchoolMonitoringTab from "@/components/DataEntrySchoolMonitoringTab";
import DataEntryQualityAuditTab from "@/components/DataEntryQualityAuditTab";
import DataEntryGuidanceChip from "@/components/DataEntryGuidanceChip";
import { useDataEntryAiGuidance } from "@/hooks/useDataEntryAiGuidance";
import { computeCriticalSchoolCount } from "@/utils/dataEntryDerived";
import "./DataEntryDashboard.css";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  LineChart,
  Line
} from "recharts";

import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// KPI Card Component
const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description, trend }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    cyan: "bg-cyan-50 text-cyan-600",
  };

  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
            {trend && (
              <p className={`text-xs mt-1 ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% from PY
              </p>
            )}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colors[color]}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// RAG Badge
const RAGBadge = ({ value, thresholds = { green: 99, amber: 95 } }) => {
  let color = "bg-emerald-100 text-emerald-700";
  if (value < thresholds.amber) color = "bg-red-100 text-red-700";
  else if (value < thresholds.green) color = "bg-amber-100 text-amber-700";
  
  return <Badge className={color}>{value}%</Badge>;
};

const DataEntryDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [blockData, setBlockData] = useState([]);
  const [completionBands, setCompletionBands] = useState(null);
  const [certificationData, setCertificationData] = useState(null);
  const [repeaterData, setRepeaterData] = useState([]);
  const [criticalSchools, setCriticalSchools] = useState([]);
  const [highRepeaterSchools, setHighRepeaterSchools] = useState([]);
  const [dataQuality, setDataQuality] = useState(null);
  const [topBottomBlocks, setTopBottomBlocks] = useState(null);
  const [activeTab, setActiveTab] = useState("executive");

  const scopeParams = useMemo(() => {
    const p = {};
    if (scope.districtCode?.trim()) p.district_code = scope.districtCode.trim();
    if (scope.districtName?.trim()) p.district_name = scope.districtName.trim();
    if (scope.blockCode?.trim()) p.block_code = scope.blockCode.trim();
    if (scope.blockName?.trim()) p.block_name = scope.blockName.trim();
    if (scope.udiseCode?.trim()) p.udise_code = scope.udiseCode.trim();
    if (scope.schoolName?.trim()) p.school_name = scope.schoolName.trim();
    return p;
  }, [scope.districtCode, scope.districtName, scope.blockCode, scope.blockName, scope.udiseCode, scope.schoolName]);

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        blockRes,
        bandsRes,
        certRes,
        repeaterRes,
        criticalRes,
        highRepeaterRes,
        qualityRes,
        topBottomRes
      ] = await Promise.all([
        axios.get(`${API}/data-entry/overview`, { params: scopeParams }),
        axios.get(`${API}/data-entry/block-wise`, { params: scopeParams }),
        axios.get(`${API}/data-entry/school-completion-bands`, { params: scopeParams }),
        axios.get(`${API}/data-entry/certification-status`, { params: scopeParams }),
        axios.get(`${API}/data-entry/repeater-analysis`, { params: scopeParams }),
        axios.get(`${API}/data-entry/critical-schools`, { params: scopeParams }),
        axios.get(`${API}/data-entry/high-repeater-schools`, { params: scopeParams }),
        axios.get(`${API}/data-entry/data-quality`, { params: scopeParams }),
        axios.get(`${API}/data-entry/top-bottom-blocks`, { params: scopeParams })
      ]);
      
      setOverview(overviewRes.data);
      setBlockData(blockRes.data);
      setCompletionBands(bandsRes.data);
      setCertificationData(certRes.data);
      setRepeaterData(repeaterRes.data);
      setCriticalSchools(criticalRes.data);
      setHighRepeaterSchools(highRepeaterRes.data);
      setDataQuality(qualityRes.data);
      setTopBottomBlocks(topBottomRes.data);
    } catch (error) {
      console.error("Error fetching Data Entry data:", error);
      toast.error("Failed to load data entry status data");
    } finally {
      setLoading(false);
    }
  }, [scope.version, scopeParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImport = async () => {
    setImporting(true);
    try {
      await axios.post(`${API}/data-entry/import`);
      toast.success("Data Entry Status import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 5000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const criticalCount = useMemo(
    () => computeCriticalSchoolCount(completionBands, criticalSchools),
    [completionBands, criticalSchools]
  );

  const scopeKey = scope.version ?? `${scope.districtCode}-${scope.blockCode}-${scope.udiseCode}`;

  const {
    guidance,
    provider: guidanceProvider,
    loading: guidanceLoading,
    refresh: refreshGuidance,
    generatedAt: guidanceGeneratedAt,
  } = useDataEntryAiGuidance({
    overview,
    blockData,
    completionBands,
    criticalCount,
    criticalSchools,
    highRepeaterSchools,
    dataQuality,
    certificationData,
    topBottomBlocks,
    scopeLabel,
    scopeKey,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = overview && overview.total_schools > 0;

  const tabItems = [
    { id: "executive", label: "Executive View", icon: BarChart3 },
    { id: "blocks", label: "Block Performance", icon: Target },
    { id: "schools", label: "School Monitoring", icon: School },
    { id: "quality", label: "Quality & Audit", icon: ShieldCheck },
    { id: "insights", label: "Insights", icon: Brain },
  ];

  const tabSubtitles = {
    executive:
      "Executive view for completion, certification, pending student records, repeater validation and block-wise data quality accountability.",
    blocks:
      "Block performance view redesigned to help officers quickly identify certification gaps, pending-load hotspots and repeater-heavy blocks.",
    schools: "School-wise entry monitoring · Real-Time · Pune District · 2025–26",
    quality:
      "School-wise entry monitoring with audit confidence, certification bottlenecks, exception alerts, and officer-ready action queues for clean and certified data.",
    insights: "School-wise entry monitoring · Real-Time · Pune District · 2025–26",
  };

  return (
    <div className="de-dashboard animate-fade-in" data-testid="data-entry-dashboard">
      <section className="de-page-header">
        <div>
          <div className="de-eyebrow">
            {activeTab === "blocks" || activeTab === "schools" || activeTab === "insights" ? (
              <BarChart3 size={18} />
            ) : activeTab === "quality" ? (
              <ShieldCheck size={18} />
            ) : (
              <FileCheck size={18} />
            )}
            {activeTab === "quality"
              ? "Quality assurance · Audit controls"
              : activeTab === "blocks" || activeTab === "schools" || activeTab === "insights"
                ? "School-wise Entry Monitoring"
                : "Real-time school-wise entry monitoring"}{" "}
            · {scopeLabel} · 2025–26
          </div>
          <h1>Data Entry Status Dashboard</h1>
          <p>{tabSubtitles[activeTab] ?? tabSubtitles.executive}</p>
          {hasData && (
            <div className="de-status-row" style={{ marginTop: "0.75rem" }}>
              <DataEntryGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
            </div>
          )}
        </div>
        <div className="de-header-actions">
          <button
            type="button"
            className="de-ghost-action"
            onClick={handleImport}
            disabled={importing}
            data-testid="import-data-entry-btn"
          >
            {importing ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
            Import Data
          </button>
          <ExportPanel dashboardName="data-entry" dashboardTitle="Data Entry Status" ghostAction />
          <button type="button" className="de-primary-action" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </section>

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FileCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Data Entry Status Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the Data Entry Status Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-data-entry-empty-btn">
              {importing ? "Importing..." : "Import Data Entry Status Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="de-tabs-wrap" aria-label="Data entry dashboard sections">
              <nav className="de-tabs">
                {tabItems.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`de-tab ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                    data-testid={`tab-${id}`}
                  >
                    <Icon size={17} />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <TabsContent value="executive" className="mt-0">
              <DataEntryExecutiveTab
                overview={overview}
                completionBands={completionBands}
                blockData={blockData}
                topBottomBlocks={topBottomBlocks}
                guidance={guidance?.executive}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                onNavigateTab={setActiveTab}
                scopeLabel={scopeLabel}
              />
            </TabsContent>

            <TabsContent value="blocks" className="mt-0">
              <DataEntryBlockPerformanceTab
                overview={overview}
                completionBands={completionBands}
                blockData={blockData}
                guidance={guidance?.blocks}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                onNavigateTab={setActiveTab}
              />
            </TabsContent>

            <TabsContent value="schools" className="mt-0">
              <DataEntrySchoolMonitoringTab
                overview={overview}
                criticalSchools={criticalSchools}
                criticalCount={criticalCount}
                highRepeaterSchools={highRepeaterSchools}
                certificationData={certificationData}
                scopeLabel={scopeLabel}
                guidance={guidance?.schools}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
              />
            </TabsContent>

            <TabsContent value="quality" className="mt-0">
              <DataEntryQualityAuditTab
                overview={overview}
                dataQuality={dataQuality}
                certificationData={certificationData}
                blockData={blockData}
                topBottomBlocks={topBottomBlocks}
                guidance={guidance?.quality}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
              />
            </TabsContent>
            <TabsContent value="insights" className="mt-0">
              <DataEntryInsightsTab
                overview={overview}
                criticalCount={criticalCount}
                blockData={blockData}
                scopeLabel={scopeLabel}
                guidance={guidance?.insights}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                guidanceGeneratedAt={guidanceGeneratedAt}
                onRefreshGuidance={refreshGuidance}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default DataEntryDashboard;
